import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { CreditCard, Plus, Pencil, Trash2 } from "lucide-react";

import { useSearch } from "../../context/Search/SearchContext";
import { useCurrencyContext } from "../../context/Currency/CurrencyContext";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import PlanModal from "../PlanModal";
import TableRowSkeleton from "../TableRowSkeleton";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "hidden",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark
        ? "rgba(255,255,255,0.06)"
        : "rgba(15,23,42,0.06)",
      color:
        dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    newBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 16px",
      gap: 6,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
    mainPaper: {
      flex: 1,
      minHeight: 0,
      padding: 0,
      overflow: "hidden",
      borderRadius: 12,
      background:
        dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowY: "auto",
      overflowX: "auto",
      ...(theme.scrollbarStyles || {}),
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${
          dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")
        }`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
        whiteSpace: "nowrap",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color:
          dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${
          dsColors.border || (isDark ? "#202632" : "#e8e8e8")
        }`,
        padding: "12px 16px",
        position: "sticky",
        top: 0,
        zIndex: 3,
        whiteSpace: "nowrap",
      },
    },
    nameHeadCell: {
      minWidth: 200,
    },
    flagHeadCell: {
      minWidth: 130,
    },
    cyberpunkCheckbox: {
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      width: 16,
      height: 16,
      border: `2px solid ${theme.palette.primary.main}`,
      borderRadius: 5,
      backgroundColor: "transparent",
      display: "inline-block",
      position: "relative",
      margin: 0,
      padding: 0,
      cursor: "pointer",
      flexShrink: 0,
      verticalAlign: "middle",
      "&::before": {
        content: '""',
        backgroundColor: theme.palette.primary.main,
        display: "block",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%) scale(0)",
        width: 8,
        height: 8,
        borderRadius: 3,
        transition: "all 0.25s ease-in-out",
      },
      "&:checked::before": {
        transform: "translate(-50%, -50%) scale(1)",
      },
      "&:indeterminate::before": {
        transform: "translate(-50%, -50%) scale(1)",
        width: 10,
        height: 2,
        borderRadius: 1,
      },
    },
    selectCell: {
      width: 52,
      padding: "0 12px 0 16px !important",
      textAlign: "center",
    },
    bulkDeleteBtn: {
      position: "absolute",
      top: 11,
      left: 64,
      background: "linear-gradient(135deg, #e53935 0%, #c62828 100%)",
      color: "#fff",
      width: 30,
      height: 30,
      borderRadius: 10,
      border: "none",
      boxShadow: "0 4px 12px rgba(229,57,53,0.35)",
      zIndex: 1100,
      padding: 0,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "scale(1.08)",
        boxShadow: "0 6px 18px rgba(229,57,53,0.45)",
      },
      "&:disabled": {
        cursor: "not-allowed",
        opacity: 0.7,
      },
    },
    bulkDeleteBadge: {
      position: "absolute",
      top: -6,
      right: -6,
      background: "#fff",
      color: "#e53935",
      fontSize: 10,
      fontWeight: 800,
      minWidth: 16,
      height: 16,
      borderRadius: 8,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 4px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      lineHeight: 1,
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor:
          dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    nameCell: {
      fontWeight: 600,
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      marginRight: 4,
      transition: "all 0.18s ease",
      color:
        dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark
          ? "rgba(255,255,255,0.06)"
          : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: "#e53935",
      "&:hover": {
        background: "rgba(229,57,53,0.10)",
        color: "#c62828",
      },
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: isDark ? "#666" : "#999",
      fontSize: 13.5,
    },
    booleanYes: {
      color: isDark ? "#81c784" : "#2e7d32",
      fontWeight: 600,
    },
    booleanNo: {
      color: isDark ? "#ef9a9a" : "#c62828",
      fontWeight: 600,
    },
  };
});

const PlansManager = () => {
  const classes = useStyles();
  const { ticketSearchParam, clear } = useSearch();
  const { getAvailableCurrencies } = useCurrencyContext();
  const { list, save, update, remove } = usePlans();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [confirmDeleteManyOpen, setConfirmDeleteManyOpen] = useState(false);

  const allCurrencies = getAvailableCurrencies();
  const currencyMap = useMemo(
    () =>
      allCurrencies.reduce((acc, c) => {
        acc[c.code] = c;
        return acc;
      }, {}),
    [allCurrencies]
  );

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await list();
      setRecords(data || []);
    } catch (e) {
      toast.error(i18n.t("settings.toasts.recordsLoadError"));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPlans();
    return () => clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = (ticketSearchParam || "").trim().toLowerCase();
    if (!term) return records;
    return records.filter((p) => p.name?.toLowerCase().includes(term));
  }, [records, ticketSearchParam]);

  const selectableIds = useMemo(
    () => filtered.filter((p) => p.id !== 1).map((p) => p.id),
    [filtered]
  );

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => selectableIds.includes(id)));
  }, [selectableIds]);

  useEffect(() => {
    setIsSelectAllChecked(
      selectableIds.length > 0 &&
        selectedIds.length === selectableIds.length
    );
  }, [selectedIds, selectableIds]);

  const handleToggleSelect = (id) => (event) => {
    if (event.target.checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(selectableIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleConfirmDeleteMany = async () => {
    if (selectedIds.length === 0) {
      setConfirmDeleteManyOpen(false);
      return;
    }
    setLoading(true);
    try {
      await Promise.all(selectedIds.map((id) => remove(id)));
      toast.success(i18n.t("settings.toasts.operationSuccess"));
      setSelectedIds([]);
      setIsSelectAllChecked(false);
      setConfirmDeleteManyOpen(false);
      await loadPlans();
    } catch (e) {
      toast.error(i18n.t("settings.toasts.operationDeleteError"));
    }
    setLoading(false);
  };

  const handleNew = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleEdit = (plan) => {
    setSelected(plan);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelected(null);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCloseModal();
      toast.success(i18n.t("settings.toasts.operationSuccess"));
    } catch (e) {
      toast.error(i18n.t("settings.toasts.planOperationError"));
    }
    setLoading(false);
  };

  const handleAskDeleteFromModal = () => {
    setPendingDelete(selected);
    setConfirmDelete(true);
  };

  const handleAskDeleteFromRow = (plan) => {
    setPendingDelete(plan);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete?.id) {
      setConfirmDelete(false);
      return;
    }
    setLoading(true);
    try {
      await remove(pendingDelete.id);
      await loadPlans();
      setConfirmDelete(false);
      setPendingDelete(null);
      handleCloseModal();
      toast.success(i18n.t("settings.toasts.operationSuccess"));
    } catch (e) {
      toast.error(i18n.t("settings.toasts.operationDeleteError"));
    }
    setLoading(false);
  };

  const formatPlanAmount = (row) => {
    const amount = parseFloat(row.amount) || 0;
    const curr =
      currencyMap[row.currency] ||
      currencyMap["USD"] ||
      { code: "USD", locale: "en-US" };
    try {
      return new Intl.NumberFormat(curr.locale, {
        style: "currency",
        currency: curr.code,
      }).format(amount);
    } catch {
      return `${curr.symbol || "$"} ${amount.toFixed(2)}`;
    }
  };

  const renderBool = (val) =>
    val === false ? (
      <span className={classes.booleanNo}>{i18n.t("plans.form.no")}</span>
    ) : (
      <span className={classes.booleanYes}>{i18n.t("plans.form.yes")}</span>
    );

  return (
    <div className={classes.container}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <CreditCard size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {i18n.t("plans.title")}
              <span className={classes.countChip}>{filtered.length}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {i18n.t("plans.subtitle")}
            </div>
          </div>
        </div>

        <Button className={classes.newBtn} onClick={handleNew}>
          <Plus size={16} strokeWidth={2} />
          {i18n.t("plans.actions.new")}
        </Button>
      </div>

      <Paper className={classes.mainPaper} variant="outlined" elevation={0}>
        <div className={classes.tableScroll} style={{ position: "relative" }}>
          {selectedIds.length > 0 && (
            <button
              type="button"
              className={classes.bulkDeleteBtn}
              onClick={() => setConfirmDeleteManyOpen(true)}
              disabled={loading}
              title={i18n.t("contacts.bulkActions.deleteSelected", {
                count: selectedIds.length,
              })}
              aria-label={i18n.t("contacts.bulkActions.deleteSelected", {
                count: selectedIds.length,
              })}
            >
              <Trash2 size={14} strokeWidth={2.2} />
              <span className={classes.bulkDeleteBadge}>
                {selectedIds.length}
              </span>
            </button>
          )}
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="center" className={classes.selectCell}>
                  <input
                    type="checkbox"
                    className={classes.cyberpunkCheckbox}
                    checked={isSelectAllChecked}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          selectedIds.length > 0 && !isSelectAllChecked;
                      }
                    }}
                    onChange={handleSelectAll}
                    disabled={selectableIds.length === 0}
                    aria-label={i18n.t("contacts.table.selectAll")}
                  />
                </TableCell>
                <TableCell align="left" className={classes.nameHeadCell}>
                  {i18n.t("plans.form.name")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("plans.form.users")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("plans.form.connections")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("settings.managers.plans.queues")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("settings.managers.plans.value")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("settings.currency")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("settings.managers.plans.whatsapp")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("settings.managers.plans.facebook")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("settings.managers.plans.instagram")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("plans.form.campaigns")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("plans.form.schedules")}
                </TableCell>
                <TableCell align="center" className={classes.flagHeadCell}>
                  {i18n.t("settings.managers.plans.externalAPI")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("settings.managers.plans.kanban")}
                </TableCell>
                <TableCell align="center" className={classes.flagHeadCell}>
                  {i18n.t("settings.managers.plans.officialAPI")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("settings.managers.plans.integrations")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("plans.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && records.length === 0 ? (
                <TableRowSkeleton columns={17} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={17} className={classes.emptyState}>
                    {ticketSearchParam
                      ? i18n.t("plans.empty.search")
                      : i18n.t("plans.empty.list")}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow key={row.id} className={classes.tableRow}>
                    <TableCell align="center" className={classes.selectCell}>
                      {row.id !== 1 && (
                        <input
                          type="checkbox"
                          className={classes.cyberpunkCheckbox}
                          checked={selectedIds.includes(row.id)}
                          onChange={handleToggleSelect(row.id)}
                          aria-label={`select-${row.name || row.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell align="left" className={classes.nameCell}>
                      {row.name || "-"}
                    </TableCell>
                    <TableCell align="center">{row.users || "-"}</TableCell>
                    <TableCell align="center">
                      {row.connections || "-"}
                    </TableCell>
                    <TableCell align="center">{row.queues || "-"}</TableCell>
                    <TableCell align="center">
                      {formatPlanAmount(row)}
                    </TableCell>
                    <TableCell align="center">
                      {row.currency || "BRL"}
                    </TableCell>
                    <TableCell align="center">
                      {renderBool(row.useWhatsapp)}
                    </TableCell>
                    <TableCell align="center">
                      {renderBool(row.useFacebook)}
                    </TableCell>
                    <TableCell align="center">
                      {renderBool(row.useInstagram)}
                    </TableCell>
                    <TableCell align="center">
                      {renderBool(row.useCampaigns)}
                    </TableCell>
                    <TableCell align="center">
                      {renderBool(row.useSchedules)}
                    </TableCell>
                    <TableCell align="center">
                      {renderBool(row.useExternalApi)}
                    </TableCell>
                    <TableCell align="center">
                      {renderBool(row.useKanban)}
                    </TableCell>
                    <TableCell align="center">
                      {renderBool(row.useOfficialAPI)}
                    </TableCell>
                    <TableCell align="center">
                      {renderBool(row.useIntegrations)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        className={classes.iconActionBtn}
                        onClick={() => handleEdit(row)}
                        aria-label="edit"
                      >
                        <Pencil size={16} strokeWidth={1.8} />
                      </IconButton>
                      {row.id !== 1 && (
                        <IconButton
                          size="small"
                          className={classes.deleteActionBtn}
                          onClick={() => handleAskDeleteFromRow(row)}
                          aria-label="delete"
                        >
                          <Trash2 size={16} strokeWidth={1.8} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>

      <PlanModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        onDelete={handleAskDeleteFromModal}
        initialValue={selected}
        loading={loading}
      />

      <ConfirmationModal
        title={
          pendingDelete
            ? `${i18n.t("settings.modals.deleteTitle")} ${pendingDelete.name}?`
            : i18n.t("settings.modals.deleteTitle")
        }
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      >
        {i18n.t("settings.modals.deleteConfirmation")}
      </ConfirmationModal>

      <ConfirmationModal
        title={i18n.t("contacts.bulkActions.deleteConfirmTitle", {
          count: selectedIds.length,
        })}
        open={confirmDeleteManyOpen}
        onClose={() => setConfirmDeleteManyOpen(false)}
        onConfirm={handleConfirmDeleteMany}
      >
        {i18n.t("contacts.bulkActions.deleteConfirmMessage")}
      </ConfirmationModal>
    </div>
  );
};

export default PlansManager;
